import { ErrorAlert } from "@components/ErrorAlert";
import { queriesPayableRows, queriesPayers, queriesPayrollRuns } from "@query";
import {
  useIsMobile,
  WSActions,
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSInfiniteScroll,
  WSList,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  Currency,
  PaymentMethodRef,
  PayrollRunFailureReason
} from "@wingspanhq/wingspan-api-sdk";
import React, { useCallback, useMemo, useState } from "react";
import {
  Route,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom";

import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { CompletedDeductionDetails } from "../../../Invoices/screens/deductions/CompletedDuductionDetails";
import { ScheduledDeductionDetails } from "../../../Invoices/screens/deductions/ScheduledDuductionDetails";
import { PayableDetails } from "../../../Invoices/screens/payables/PayableDetails";
import { Summary } from "../../../Invoices/components/Summary/Summary";
import { LayoutSetup } from "../../../shared/components/LayoutSetup";
import { selectorPaymentMethodRefMethodType } from "../../../shared/selectors/selectorPaymentMethodRefMethodType";
import { useMethodLabelProps } from "../../Settings/components/LabelMethod/useMethodLabelProps";
import {
  PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD,
  PATH_SETTINGS_PAYROLL_METHOD
} from "../../Settings/paths";
import { AlertContractorPayrollRunInProgress } from "../components/AlertContractorPayrollRunInProgress";
import { TablePayables } from "../components/TablePayables/TablePayables";
import { getTabFilter } from "../components/TablePayables/filters/request";
import { PATH_PAYABLES, routeHelpersPayablesPayrollRun } from "../paths";
import { selectorOffCyclePayrollPayables } from "../selectors/selectorOffCyclePayrollPayables";
import { selectorOffCyclePayrollTotals } from "../selectors/selectorOffCyclePayrollTotals";
import { selectorPayrollFundingCurrencies } from "../selectors/selectorPayrollFundingCurrencies";
import { selectorPayrollFundingSource } from "../selectors/selectorPayrollFundingSource";
import { selectorPayrollRunFailureAlert } from "../../PayrollRuns/selectors/selectorPayrollRunFailureAlert";
import { runOffCyclePayroll } from "../utils/runOffCyclePayroll";

const FundingSourceSummary: React.FC<{
  fundingSource?: PaymentMethodRef;
}> = props => {
  const history = useHistory();
  const methodType = selectorPaymentMethodRefMethodType(
    props.fundingSource?.type
  );
  const { label } = useMethodLabelProps(props.fundingSource?.id, methodType);

  return (
    <Summary
      label={
        <WSButton.Link
          rightIcon="arrow-right"
          size="S"
          onClick={() => history.push(PATH_SETTINGS_PAYROLL_METHOD)}
        >
          Payroll funding method
        </WSButton.Link>
      }
      value={label?.text ?? "–"}
    />
  );
};

/**
 * Run off-cycle contractor payroll — pay everything already approved, outside
 * the regular schedule.
 *
 * v1 did this with one opaque `POST /pay-approved`. v3 splits it into a run you
 * can see before committing to it: `createOffCyclePayrollRun` with
 * `selection.scope = ApprovedPayables` produces a `Draft` holding the payables,
 * and `finalizePayrollRun` is the money-moving verb (step-up MFA is handled by
 * `mfaChallengeMiddleware`, so neither call needs to know about it).
 *
 * A failed finalize has two deployed outcomes and the screen has to tell them
 * apart, because they need opposite recoveries — see `handleRunPayroll`.
 */
export const RoutePayablesOffCyclePayroll: React.FC = () => {
  useBrowserPageTitle("Run off-cycle payroll");

  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const match = useRouteMatch();
  const goToPayrollRun = routeHelpersPayablesPayrollRun.useGoTo();

  const queryPayerSettings = queriesPayers.getCurrentPayerSettings.useQuery();
  const payrollSettings = queryPayerSettings.data?.payrollSettings;

  const fundingCurrencies = useMemo(
    () => selectorPayrollFundingCurrencies(payrollSettings),
    [payrollSettings]
  );
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
    null
  );
  const currency =
    selectedCurrency ??
    queryPayerSettings.data?.defaultAccountCurrency ??
    fundingCurrencies[0] ??
    Currency.USD;

  // Paginated, as v1 was, and as every other payables table is. An earlier
  // revision read the whole eligible set with `useQueryAll` to compute the
  // tiles client-side; that is unbounded on a payer with many approved payables,
  // and unnecessary — `ApprovedPayables` pays the whole set regardless of what
  // this screen has loaded, so the server's own summary is both exact and free.
  const queryPayables = queriesPayableRows.searchPayableRows.usePaginatedQuery(
    getTabFilter("scheduled")
  );

  const payables = useMemo(
    () =>
      selectorOffCyclePayrollPayables({
        payables: queryPayables.flatData ?? [],
        currency
      }),
    [queryPayables.flatData, currency]
  );

  // The summary spans every currency the filter matched. That is exact for a
  // single-currency payer (all of them today) and overstates for a payer whose
  // approved payables span currencies, which needs a currency filter on
  // `searchPayableRows` to fix properly.
  const summary = queryPayables.data?.pages?.[0]?.summary;
  const totals = useMemo(
    () =>
      summary
        ? { count: summary.count, amount: summary.amountTotal }
        : selectorOffCyclePayrollTotals(payables),
    [summary, payables]
  );

  const fundingSource = selectorPayrollFundingSource({
    payrollSettings,
    currency
  });

  const [createPayrollRun, createMeta] =
    queriesPayrollRuns.createOffCyclePayrollRun.useMutation();
  const [finalizePayrollRun, finalizeMeta] =
    queriesPayrollRuns.finalizePayrollRun.useMutation();

  /**
   * A `Draft` run left behind by a refused finalize. Held so the retry
   * re-finalizes that run instead of creating a second one competing for the
   * same payables.
   */
  const [draftRunId, setDraftRunId] = useState<string | null>(null);

  /**
   * The refused run's own reason, which is more specific than the transport
   * error's `detailCode` — see `OffCyclePayrollOutcome`.
   */
  const [failureReason, setFailureReason] = useState<
    PayrollRunFailureReason | undefined
  >();

  const handleRunPayroll = useCallback(async () => {
    const outcome = await runOffCyclePayroll({
      draftRunId,
      currency,
      createPayrollRun,
      finalizePayrollRun,
      readPayrollRun: queriesPayrollRuns.getPayrollRun.forceFetchQueryData
    });

    setDraftRunId(outcome.type === "retryable" ? outcome.payrollRunId : null);
    setFailureReason(
      outcome.type === "paid" ? undefined : outcome.failureReason
    );

    if (outcome.type === "paid") {
      goToPayrollRun({ payrollRunId: outcome.payrollRunId });
    }
  }, [
    createPayrollRun,
    currency,
    draftRunId,
    finalizePayrollRun,
    goToPayrollRun
  ]);

  const [deletePayrollRun, deleteMeta] =
    queriesPayrollRuns.deletePayrollRun.useMutation();

  const handleDiscardDraft = useCallback(async () => {
    if (!draftRunId) {
      return;
    }
    await deletePayrollRun({ payrollRunId: draftRunId });
    setDraftRunId(null);
    setFailureReason(undefined);
  }, [deletePayrollRun, draftRunId]);

  const failureAlert = failureReason
    ? selectorPayrollRunFailureAlert({ failureReason })
    : undefined;

  const isRunning = createMeta.isLoading || finalizeMeta.isLoading;
  const canRunPayroll = !!fundingSource && (!!draftRunId || totals.count > 0);

  return (
    <LayoutSetup wide onBack={() => history.push(PATH_PAYABLES)}>
      <WSList gap="2XL">
        <AlertContractorPayrollRunInProgress />

        <WSElement>
          <WSText.Heading4>Run off-cycle payroll</WSText.Heading4>
          <WSText>
            Payables eligible for payroll must be approved and awaiting payment.
          </WSText>
        </WSElement>

        <WSGrid>
          {/*
           * v1's labels, values included: "Total 1099s" carries the amount and
           * "Total amount" the count. They read swapped, and the swap is filed
           * as PLA-502 — deliberately iceboxed, so it is not changed here.
           */}
          <WSGrid.Item span={{ xs: "6", m: "3" }}>
            <Summary
              label="Total 1099s"
              value={formatMoney(totals.amount, currency)}
            />
          </WSGrid.Item>

          <WSGrid.Item span={{ xs: "6", m: "3" }}>
            <Summary label="Total amount" value={totals.count} />
          </WSGrid.Item>

          <WSGrid.Item span={{ xs: "6", m: "3" }}>
            <FundingSourceSummary fundingSource={fundingSource} />
          </WSGrid.Item>

          {fundingCurrencies.length > 1 && (
            <WSGrid.Item span={{ xs: "6", m: "3" }}>
              <WSText.ParagraphSm color="gray500" mb="XS">
                Currency
              </WSText.ParagraphSm>
              <WSSelect
                mode="single"
                hideClearAction
                options={fundingCurrencies.map(fundingCurrency => ({
                  label: fundingCurrency,
                  value: fundingCurrency
                }))}
                value={currency}
                onChange={value => setSelectedCurrency(value as Currency)}
              />
            </WSGrid.Item>
          )}
        </WSGrid>

        {!fundingSource && !queryPayerSettings.isInitialLoading && (
          <WSAlert
            action={{
              text: "Add funding method",
              onClick: () =>
                history.push(PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD, {
                  backPath: location.pathname
                })
            }}
            icon="alert"
            theme="warning"
            title="Funding method is missing"
          >
            Add a payroll funding method for {currency} to enable payroll.
          </WSAlert>
        )}

        {(draftRunId || failureReason) && (
          <WSAlert
            icon="alert"
            theme="warning"
            title={failureAlert?.title ?? "Payroll run failed"}
          >
            {failureAlert?.description ??
              "The payroll run was created but could not be funded."}
            {draftRunId &&
              " Fix the cause, then retry — retrying finalizes the same run, so the payables stay in it."}
          </WSAlert>
        )}

        <WSElement>
          <WSGrid>
            <WSGrid.Item span={{ s: "6", m: "8" }}>
              <WSText.Heading5>Included payables</WSText.Heading5>
              <WSText>
                To change what is included, edit a payable&apos;s approval or
                pay date.
              </WSText>
            </WSGrid.Item>

            <WSGrid.Item span={{ s: "6", m: "4" }}>
              <WSFlexBox.CenterY justify="flex-end">
                <WSActions
                  alignment={isMobile ? "fill" : "right"}
                  buttons={[
                    {
                      type: "button",
                      kind: "Secondary",
                      label: "Discard draft run",
                      visible: !!draftRunId,
                      loading: deleteMeta.isLoading,
                      onClick: handleDiscardDraft
                    },
                    {
                      type: "button",
                      label: draftRunId ? "Retry payroll run" : "Run payroll",
                      disabled: !canRunPayroll,
                      loading: isRunning,
                      onClick: handleRunPayroll
                    }
                  ]}
                />
              </WSFlexBox.CenterY>
            </WSGrid.Item>
          </WSGrid>
        </WSElement>

        <ErrorAlert context="OffCyclePayroll" error={createMeta.error} />
        {/*
         * Suppressed once the run itself named a reason: the alert above is
         * already showing that, and the transport error only resolves to the
         * error map's catch-all, so rendering both says the same thing twice —
         * once vaguely.
         */}
        {!failureReason && (
          <ErrorAlert context="PayrollFinalize" error={finalizeMeta.error} />
        )}

        <Route
          path={`${match.path}/:payableId/deduction/scheduled/:deductionId`}
          component={ScheduledDeductionDetails}
        />
        <Route
          path={`${match.path}/:payableId/deduction/completed/:deductionId`}
          component={CompletedDeductionDetails}
        />
        <Route
          exact
          path={`${match.path}/:payableId`}
          component={PayableDetails}
        />

        <WSElement>
          <WSInfiniteScroll
            endOfList={!queryPayables.hasNextPage}
            loading={!!queryPayables.isFetchingNextPage}
            loadMore={payables.length > 0}
            onLoad={() => queryPayables.fetchNextPage()}
          >
            <TablePayables
              loading={queryPayables.isInitialLoading}
              payables={payables}
              onRowClick={payable => {
                if (payable.id) {
                  history.push(`${match.url}/${payable.id}`);
                }
              }}
              visibleColumns={{
                payee: true,
                payableNumber: true,
                amount: true,
                dueDate: true,
                status: true
              }}
            />
          </WSInfiniteScroll>
          {!queryPayables.isInitialLoading && payables.length === 0 && (
            <WSFlexBox justify="center" mt="XL">
              <WSText color="gray600">
                No payables available for off-cycle payroll
              </WSText>
            </WSFlexBox>
          )}
        </WSElement>
      </WSList>
    </LayoutSetup>
  );
};
